/**
 * ProtectedRoute Component
 * Route guard that checks authentication and user role before allowing access.
 * Redirects to login if not authenticated, shows 403 if wrong role.
 * Shows a verification wall if the user's email is not yet confirmed, and
 * automatically detects cross-device verification via polling.
 */

import React, { useState, useEffect, useCallback } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import {
  Box, Typography, Button, Container, Alert,
  CircularProgress, Fade,
} from '@mui/material';
import MarkEmailUnreadIcon from '@mui/icons-material/MarkEmailUnread';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import { useAuth } from '../context/AuthContext';
import LoadingSpinner from './LoadingSpinner';
import api from '../services/api'; // used for resendVerification

const BRAND = '#B71C1C';
const POLL_INTERVAL_MS = 5000; // check every 5 seconds

// ── Verification wall — own component so hooks are unconditional ──────────────

function EmailVerificationWall({ user, refreshUser }) {
  const [resending, setResending] = useState(false);
  const [resendMsg, setResendMsg] = useState('');
  const [checking, setChecking] = useState(false);
  const [justVerified, setJustVerified] = useState(false);

  // Silently poll /auth/me until isEmailVerified flips to true
  const checkVerified = useCallback(async (silent = true) => {
    if (!silent) setChecking(true);
    try {
      const fresh = await refreshUser();
      if (fresh?.isEmailVerified) {
        setJustVerified(true); // brief success flash; AuthContext re-render will clear the wall
      }
    } catch { /* network error — stay on wall */ } finally {
      if (!silent) setChecking(false);
    }
  }, [refreshUser]);

  // Auto-poll while wall is mounted
  useEffect(() => {
    const interval = setInterval(() => checkVerified(true), POLL_INTERVAL_MS);
    return () => clearInterval(interval);
  }, [checkVerified]);

  const handleResend = async () => {
    setResending(true);
    setResendMsg('');
    try {
      await api.auth.resendVerification(user.email);
      setResendMsg('A new verification link has been sent — check your inbox (and spam folder).');
    } catch (err) {
      const msg = err?.response?.data?.message || 'Could not send the email right now. Please try again in a moment.';
      setResendMsg(msg);
    } finally {
      setResending(false);
    }
  };

  // Brief "verified!" flash before AuthContext re-renders the real page
  if (justVerified) {
    return (
      <Box sx={{ minHeight: '100vh', backgroundColor: '#f5f5f5', display: 'flex', alignItems: 'center', justifyContent: 'center', p: 3 }}>
        <Fade in>
          <Box sx={{ backgroundColor: '#fff', borderRadius: 3, boxShadow: '0 2px 16px rgba(0,0,0,0.08)', p: { xs: 4, md: 6 }, maxWidth: 480, width: '100%', textAlign: 'center' }}>
            <CheckCircleOutlineIcon sx={{ fontSize: 64, color: '#2e7d32', mb: 2 }} />
            <Typography variant="h6" sx={{ fontWeight: 800, color: '#1a1a1a', mb: 1 }}>
              Email Verified!
            </Typography>
            <Typography variant="body2" sx={{ color: '#555' }}>
              Taking you to the platform…
            </Typography>
            <CircularProgress size={24} sx={{ mt: 2, color: BRAND }} />
          </Box>
        </Fade>
      </Box>
    );
  }

  return (
    <Box sx={{ minHeight: '100vh', backgroundColor: '#f5f5f5', display: 'flex', alignItems: 'center', justifyContent: 'center', p: 3 }}>
      <Box sx={{ backgroundColor: '#fff', borderRadius: 3, boxShadow: '0 2px 16px rgba(0,0,0,0.08)', p: { xs: 4, md: 6 }, maxWidth: 480, width: '100%', textAlign: 'center' }}>
        <Typography variant="h5" sx={{ fontWeight: 900, color: BRAND, letterSpacing: 1, mb: 4 }}>
          MyTradeWar.com
        </Typography>

        <MarkEmailUnreadIcon sx={{ fontSize: 64, color: BRAND, mb: 2 }} />

        <Typography variant="h6" sx={{ fontWeight: 800, color: '#1a1a1a', mb: 1 }}>
          Please Verify Your Email
        </Typography>
        <Typography variant="body2" sx={{ color: '#555', mb: 3, lineHeight: 1.8 }}>
          We sent a verification link to <strong>{user.email}</strong>.
          Click the link in that email to activate your account.
        </Typography>

        {/* Subtle auto-check indicator */}
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1, mb: 3 }}>
          <CircularProgress size={12} sx={{ color: '#ccc' }} />
          <Typography variant="caption" sx={{ color: '#bbb' }}>
            Waiting for verification…
          </Typography>
        </Box>

        {resendMsg && (
          <Alert
            severity={resendMsg.includes('wrong') ? 'error' : 'success'}
            sx={{ mb: 2, textAlign: 'left' }}
          >
            {resendMsg}
          </Alert>
        )}

        {/* Manual "already verified" check */}
        <Button
          variant="outlined"
          fullWidth
          onClick={() => checkVerified(false)}
          disabled={checking}
          sx={{ borderColor: BRAND, color: BRAND, fontWeight: 700, borderRadius: 2, mb: 1.5 }}
        >
          {checking
            ? <><CircularProgress size={16} sx={{ mr: 1, color: BRAND }} /> Checking…</>
            : "I've already verified — continue"}
        </Button>

        <Button
          variant="contained"
          fullWidth
          onClick={handleResend}
          disabled={resending}
          sx={{ backgroundColor: BRAND, '&:hover': { backgroundColor: '#7F0000' }, fontWeight: 700, borderRadius: 2, mb: 2 }}
        >
          {resending ? <CircularProgress size={20} color="inherit" /> : 'Resend Verification Email'}
        </Button>

        <Button
          variant="text"
          sx={{ color: '#aaa', fontSize: 12 }}
          onClick={() => { localStorage.clear(); window.location.href = '/login'; }}
        >
          Sign in with a different account
        </Button>
      </Box>
    </Box>
  );
}

// ── Main guard ────────────────────────────────────────────────────────────────

const ProtectedRoute = ({ children, requiredRole = null }) => {
  const { user, loading, isAuthenticated, refreshUser } = useAuth();
  const location = useLocation();

  if (loading) return <LoadingSpinner />;

  if (!isAuthenticated) {
    return <Navigate to={`/login?returnUrl=${encodeURIComponent(location.pathname)}`} replace />;
  }

  // Email verification gate — admins are always exempt
  if (user?.isEmailVerified === false && user?.role !== 'admin') {
    return <EmailVerificationWall user={user} refreshUser={refreshUser} />;
  }

  if (requiredRole && user?.role !== requiredRole && user?.role !== 'admin') {
    return (
      <Container maxWidth="sm" sx={{ py: 8, textAlign: 'center' }}>
        <Typography variant="h3" sx={{ mb: 2, color: '#F44336' }}>
          Access Denied
        </Typography>
        <Typography variant="body1" sx={{ mb: 4, color: '#666666' }}>
          You don't have permission to access this page.
          This page is only available to {requiredRole}s.
        </Typography>
        <Button variant="contained" href="/" sx={{ backgroundColor: BRAND }}>
          Go Home
        </Button>
      </Container>
    );
  }

  return children;
};

export default ProtectedRoute;
